<template>
  <div>
    <!-- TOP ACTION BAR (ONLY FOR COURSE) -->
    <div class="d-flex justify-content-between align-items-center mb-2" v-if="course_id">
      <h6 class="mb-0">Programme Outcome Groups</h6>

      <div>
        <!-- EDIT ICON -->
        <i v-if="!editMode" class="fas fa-edit text-info" style="cursor:pointer;font-size:18px"
          @click="enableEditMode"></i>

        <!-- SAVE / CANCEL -->
        <div v-else>
          <b-button size="sm" variant="success" class="mr-2" @click="confirmSavePoGroup">
            Save
          </b-button>

          <b-button size="sm" variant="secondary" @click="cancelEditMode">
            Cancel
          </b-button>
        </div>
      </div>
    </div>

    <b-modal id="confirmSavePoGroupModal" hide-header hide-footer centered size="sm">
      <h6 class="text-danger font-weight-bold">
        Confirm PO Group Change
      </h6>

      <p class="mt-2 mb-2 text-danger">
        All PO mapping including CO-PO-PSO mapping, Lesson Plan and activity data will be deleted when the PO Group is changed.
      </p>

      <p class="mb-2">
        Type <strong>SWAP</strong> to confirm.
      </p>

      <b-form-input v-model="swapConfirmText" placeholder="Type SWAP to continue" class="mb-3" />

      <div class="text-right">
        <b-button size="sm" variant="secondary" @click="
          $bvModal.hide('confirmSavePoGroupModal');
        selectedPoGroupId = originalPoGroupId;
        swapConfirmText = '';
        ">
          Cancel
        </b-button>

        <b-button size="sm" variant="success" class="ml-2" :disabled="swapConfirmText !== 'SWAP'"
          @click="proceedSavePoGroup">
          Yes, Save
        </b-button>
      </div>
    </b-modal>

    <!-- Add PO Group Button (NON-COURSE) -->
    <div class="mb-3">
      <b-button variant="primary" v-if="!course_id" @click="openAddGroupModal">
        Add PO Group
      </b-button>
    </div>

    <div class="groupData">
      <b-overlay :show="groupLoading" spinner-variant="primary" spinner-type="grow" spinner-small rounded="md">
        <table class="table table-bordered table-striped">
          <thead class="thead-dark">
            <tr>
              <th v-if="course_id" style="width:40px"></th>
              <th>Group Name</th>
              <th>PO</th>
              <th v-if="!course_id"></th>
            </tr>
          </thead>

          <tbody>
            <template v-for="group in groups">
              <!-- GROUP ROW -->
              <!-- v-if="course_id && (!activeGroupId || activeGroupId === group.id)" -->
              <tr :key="group.id" v-if="!activeGroupId || activeGroupId === group.id">
                <!-- RADIO -->
                <td v-if="course_id" class="text-center align-middle">
                  <b-form-radio v-model="selectedPoGroupId" :value="group.id" name="poGroup" :disabled="!editMode" />
                </td>

                <td class="align-middle">
                  {{ group.name }}
                </td>

                <td class="align-middle">
                  <span class="text-info btn p-0 m-0 font-weight-bolder" @click="togglePoDashboard(group.id)">
                    {{ group.programme_outcomes.length }} PO Added
                  </span>
                </td>

                <td v-if="!course_id" class="align-middle">
                  <i class="fas fa-edit text-info" style="cursor:pointer;font-size:18px"
                    @click="openEditGroupModal(group)"></i>
                </td>
              </tr>

              <!-- PO DETAILS -->
              <tr v-if="activeGroupId === group.id" :key="'po-' + group.id">
                <td :colspan="course_id ? 3 : 2" class="bg-light">
                  <usergroup-po-dashboard :usergroup_id="usergroupId" :po_group_id="group.id" :course_id="course_id"
                    @refresh-groups="fetchGroups" />
                </td>
              </tr>
            </template>

            <tr v-if="!groups.length && !groupLoading">
              <td :colspan="course_id ? 3 : 2" class="text-center text-muted">
                No groups found
              </td>
            </tr>
          </tbody>
        </table>
      </b-overlay>
    </div>

    <!-- ADD / EDIT MODAL -->
    <b-modal v-model="showAddGroupModal" :title="modalTitle" hide-footer>
      <b-form @submit.prevent="submitPoGroup">
        <b-form-group label="Group Name">
          <b-form-input v-model="groupName" required />
        </b-form-group>

        <div class="text-right">
          <b-button variant="secondary" @click="closeAddGroupModal">
            Cancel
          </b-button>
          <b-button variant="primary" type="submit" class="ml-2">
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import UsergroupPoDashboard from "./UsergroupPoDashboard.vue";

export default {
  props: ["course_id", "usergroupId"],

  components: { UsergroupPoDashboard },

  data() {
    return {
      groups: [],
      groupLoading: false,

      showAddGroupModal: false,
      groupName: "",
      editGroupId: null,

      activeGroupId: null,

      // COURSE MODE
      editMode: false,
      selectedPoGroupId: null,
      originalPoGroupId: null,

      swapConfirmText: "",
    };
  },

  computed: {
    modalTitle() {
      return this.editGroupId ? "Edit PO Group" : "Add PO Group";
    }
  },

  created() {
    this.fetchGroups();
  },

  methods: {
    confirmSavePoGroup() {
      // If nothing changed, just exit edit mode
      if (this.selectedPoGroupId === this.originalPoGroupId) {
        this.editMode = false;
        return;
      }

      this.$bvModal.show("confirmSavePoGroupModal");
    },

    async proceedSavePoGroup() {
      this.$bvModal.hide("confirmSavePoGroupModal");
      this.swapConfirmText = "";
      await this.saveCoursePoGroup();
    },

    async fetchGroups() {
      this.groupLoading = true;
      try {
        const url =
          this.$store.getters.getAPIKey.mainAPI +
          `/po-groups?usergroup_id=${this.usergroupId}&course_id=${this.course_id}`;

        const res = await this.$axios.get(url);

        this.groups = res.data.groups;
        this.selectedPoGroupId = res.data.course_property_po_group_id;
        this.originalPoGroupId = res.data.course_property_po_group_id;
      } finally {
        this.groupLoading = false;
      }
    },

    /* COURSE MODE CONTROLS */
    enableEditMode() {
      this.editMode = true;
    },

    cancelEditMode() {
      this.selectedPoGroupId = this.originalPoGroupId;
      this.editMode = false;
    },

    async saveCoursePoGroup() {
      try {
        await this.$axios.post(
          this.$store.getters.getAPIKey.mainAPI + "/po-groups/course-po-group",
          {
            course_id: this.course_id,
            po_group_id: this.selectedPoGroupId
          }
        );

        this.originalPoGroupId = this.selectedPoGroupId;
        this.editMode = false;
        this.$toast.success("PO Group updated successfully", {
          position: "top",
          duration: 3000,
        });

      } catch (e) {
        e;
      }
    },

    /* NON-COURSE CRUD */
    openAddGroupModal() {
      this.groupName = "";
      this.editGroupId = null;
      this.showAddGroupModal = true;
    },

    openEditGroupModal(group) {
      this.groupName = group.name;
      this.editGroupId = group.id;
      this.showAddGroupModal = true;
    },

    closeAddGroupModal() {
      this.showAddGroupModal = false;
      this.groupName = "";
      this.editGroupId = null;
    },

    async submitPoGroup() {
      if (!this.groupName) return;

      const payload = {
        name: this.groupName,
        usergroup_id: this.usergroupId
      };

      if (this.editGroupId) {
        await this.$axios.put(
          this.$store.getters.getAPIKey.mainAPI +
          `/po-groups/${this.editGroupId}`,
          payload
        );
      } else {
        await this.$axios.post(
          this.$store.getters.getAPIKey.mainAPI + "/po-groups",
          payload
        );
      }

      this.closeAddGroupModal();
      this.fetchGroups();
    },

    togglePoDashboard(groupId) {
      this.activeGroupId =
        this.activeGroupId === groupId ? null : groupId;
    }
  }
};
</script>
